import {
  ApolloClient,
  ApolloProvider,
  Operation,
  createHttpLink,
  from
} from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { onError } from "@apollo/client/link/error"
import { RetryLink } from "@apollo/client/link/retry"
import { cache } from "@pathwright/graphql-client"
import { getApolloClientVersion } from "@pathwright/utils/src/client-build/clientBuildMeta"
import {
  createApolloOperationLink,
  handleApolloOperationError,
  registerApolloClient
} from "@pathwright/utils/src/graphql/apolloOperation"
import { clearInvalidToken } from "@pathwright/utils/src/token/utils"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"
import { RefreshToken } from "../auth/refresh-token/useRefreshToken"
import { getEnv, getIsLocalDev, getPathwrightDomain } from "../lib/env"
import ApolloClientStoreBridge from "./ApolloClientStoreBridge"
import { usePathwrightContext } from "./PathwrightContext"
import {
  developmentClientLink,
  refreshedAuthTokenLink
} from "./refreshedAuthTokenLink"

export const getGraphQLEndpoint = () => {
  // Accept a full endpoint URL or a local HTTP host as well as a deployed hostname.
  const explicitHost = process.env.REACT_APP_GQL
  if (
    explicitHost &&
    (/^https?:\/\//.test(explicitHost) ||
      /^(localhost|127\.0\.0\.1)(:|$)/.test(explicitHost))
  ) {
    const base = /^https?:\/\//.test(explicitHost)
      ? explicitHost
      : `http://${explicitHost}`
    return `${base.replace(/\/$/, "")}/`
  }
  // returns the proper graphql server endpoint based on the current env

  let isLocalDev = getIsLocalDev()

  let domain = ``
  const pathwrightDomain = getPathwrightDomain()

  if (process.env.REACT_APP_GQL) {
    domain = process.env.REACT_APP_GQL
    isLocalDev = false
  } else if (pathwrightDomain) {
    // one of pathwright's domains
    domain = `gql.${pathwrightDomain}.com`
  } else {
    // custom domain
    domain = "gql.pathwright.com"
  }

  // if (isLocalDev) {
  //   // ROBERT TESTING WHILE TRAVELING
  //   return `https://gql.pathwrightstaging.com/`
  // }

  const PORT = process.env.REACT_APP_GQL_PORT || process.env.SERVER_PORT || 8000

  const base = isLocalDev
    ? `http://${window.location.hostname}:${PORT}/`
    : `https://${domain}/`

  return base
}

function isMutationOperation(operation: Operation) {
  return operation.query.definitions.some(
    (definition) =>
      definition.kind === "OperationDefinition" &&
      definition.operation === "mutation"
  )
}

type Client = ApolloClient<any> & { _consistentReadClient: ApolloClient<any> }

// A stopped client is retired once. Concurrent replacements (including effect
// replays) must await the same clear rather than clear a newly activated cache.
const retiringClients = new WeakMap<ApolloClient<any>, Promise<void>>()

/** Finish retiring the old session before mounting queries on its shared cache. */
export async function clearClientStore(
  client: ApolloClient<any> & { _consistentReadClient?: ApolloClient<any> }
) {
  const clients = new Set([client, client._consistentReadClient])
  await Promise.all(
    [...clients].map((item) => {
      if (!item) return
      let retiring = retiringClients.get(item)
      if (!retiring) {
        item.stop()
        retiring = item.clearStore().then(() => undefined)
        retiringClients.set(item, retiring)
        // A failed cleanup may be retried by a later provider mount.
        void retiring.catch(() => retiringClients.delete(item))
      }
      return retiring
    })
  )
}

// This is a singleton pattern
let _client = null as unknown as Client

type GetApolloClientOptions = {
  schoolId?: number
}

const GRAPHQL_PATH = "/graphql" as const

/** Read the active client, creating it on first use. The provider owns replacement. */
export const getApolloClient = (options: GetApolloClientOptions = {}) => {
  // If in "test" env, attempt to use the window.testClient
  // TODO: should this rather be handled via jest mocks?
  if (getEnv("test") && window.testClient && _client !== window.testClient) {
    _client = window.testClient as Client
  }

  return _client || createApolloClient(options.schoolId)
}

// Only construct a replacement after retiring the previous client's shared cache.
const createApolloClient = (schoolId?: number) => {
  const operationLink = createApolloOperationLink(GRAPHQL_PATH)

  const httpLink = createHttpLink({
    uri: (operation) => {
      let uri = `${getGraphQLEndpoint()}graphql?school_id=${schoolId}`

      // Supply an operationName as a convenience for identifying the operation
      // via the endpoint, which, for instance, is all that is included in
      // Sentry breadcrumbs. So, this can help with debugging.
      if (operation.operationName) {
        uri += `&operationName=${operation.operationName}`
      }

      return uri
    }
  })

  const schoolLink = setContext((_, { headers }) => {
    let nextHeaders = { ...headers }
    nextHeaders.school_id = schoolId
    // return the headers to the context so httpLink can read them
    return {
      headers: nextHeaders
    }
  })

  const authLink = setContext((_, { headers }) => {
    // get the authentication token from local storage if it exists
    const token = localStorage.auth_token

    let nextHeaders = { ...headers }

    if (token) {
      nextHeaders.authorization = `Bearer ${token}`
    }
    // return the headers to the context so httpLink can read them
    return {
      headers: nextHeaders
    }
  })

  // Retry transient network errors on queries. Skip mutations: composers and
  // other forms restore their draft on failure so the user can retry immediately,
  // and silent retries delay that feedback (default is 5 attempts) and can
  // double-submit if the first request actually succeeded.
  // https://www.apollographql.com/docs/link/links/retry
  const retryLink = new RetryLink({
    attempts: {
      retryIf: (error, operation) => !!error && !isMutationOperation(operation)
    }
  })

  const errorLink = onError((options) => {
    const { graphQLErrors } = options

    handleApolloOperationError(options, { graphqlPath: GRAPHQL_PATH })

    // GraphQL is on another origin, so retrying without this token is
    // anonymous, not session-authenticated. Leave for sign-in instead of
    // keeping a signed-in shell that no longer has a credential.
    if (
      graphQLErrors &&
      graphQLErrors.some(
        ({ message }) =>
          message ===
            `Invalid token. An error occurred while verifying the token: The provided token was revoked.` ||
          message ===
            `Invalid token. An error occurred while verifying the token: The provided token could not be found in the database.`
      ) &&
      localStorage.getItem("auth_token")
    ) {
      clearInvalidToken()
      return
    }
  })

  const clientOptions = {
    link: from([
      operationLink,
      retryLink,
      schoolLink,
      developmentClientLink,
      authLink,
      errorLink,
      refreshedAuthTokenLink,
      httpLink
    ]),
    cache,
    name: "web",
    version: getApolloClientVersion() ?? "1.0"
  }

  _client = new ApolloClient(clientOptions) as Client

  // Optional client used for ensuring GQL server reads data from the main database
  // as opposed to the read replica database.
  // _client._consistentReadClient = new ApolloClient({
  //   ...clientOptions,
  //   link: from([retryLink, consistentReadMiddlewareLink, httpLink])
  // })
  _client._consistentReadClient = _client

  return _client
}

export const usePathwrightClient = () => {
  const { school } = usePathwrightContext()
  const client = getApolloClient({ schoolId: school.id })
  return client
}

type PathwrightClientProps = {
  schoolId: number
  /**
   * Optional consumer-generated key (NOT the refresh token) that when changed
   * resets the client and its cached auth data without remounting its children.
   * Allows components like the sandbox to respond to auth changes
   * and clear leftover data that would cause sign-in issues..
   * School changes replace the client independently of this key.
   */
  sessionKey?: string | number
  children: JSX.Element | (({ client }: { client: Client }) => JSX.Element)
}

export const PathwrightClient = ({
  schoolId,
  sessionKey,
  children
}: PathwrightClientProps) => {
  const [client, setClient] = useState<Client | null>(null)
  const [initializationError, setInitializationError] = useState<Error | null>(
    null
  )

  useEffect(() => {
    let disposed = false
    const activateClient = () => {
      if (disposed) return
      const client = createApolloClient(schoolId)
      registerApolloClient(GRAPHQL_PATH, client)
      setClient(client)
      window.apolloClientStoreBridge = new ApolloClientStoreBridge(client)
    }
    // Clients share a cache. Finish clearing it before exposing a replacement,
    // so queries cannot read the previous identity/school or have their new
    // subscriptions removed by an outstanding clearStore operation.
    if (_client) {
      clearClientStore(_client)
        .then(activateClient)
        .catch((error) => {
          if (!disposed) setInitializationError(error)
        })
    } else activateClient()
    return () => {
      disposed = true
    }
  }, [schoolId, sessionKey])

  if (initializationError) throw initializationError

  return client ? (
    <ApolloProvider client={client}>
      <RefreshToken />
      {typeof children === "function" ? children({ client }) : children}
    </ApolloProvider>
  ) : null
}

PathwrightClient.displayName = "PathwrightClient"

PathwrightClient.propTypes = {
  schoolId: PropTypes.number.isRequired,
  onAuthChange: PropTypes.func
}

export default function withPathwrightClient(ComposedComponent: any) {
  const ComposedPathwrightClient = ({
    schoolId,
    ...props
  }: {
    schoolId: number
  }) => (
    <PathwrightClient schoolId={schoolId}>
      <ComposedComponent {...props} />
    </PathwrightClient>
  )

  return ComposedPathwrightClient
}
